import React, { useEffect, useRef, useState } from "react";
import { useFetcher } from "../../components/api";
import {
  Backdrop,
  CircularProgress,
  Container,
  Divider,
  Grid,
  makeStyles,
} from "@material-ui/core";
import { GrReport } from "../../charts/grreport";
import { useAppContext } from "../../components/appContext/app-provider";
import {
  DateFilterType,
  DateFilterTypeToString,
} from "../../components/appContext/app-model";
import {
  DateFilter,
  getComparableDateStr,
  getComparePeriodoLabel,
} from "./DateFilter";
import { CardKPI } from "./CardKPI";
import { BarChart, LineChart } from "../../charts/BarChart";
import { MyChartData } from "../../charts/MyChartData";
import { Venda, VendaMedidas } from "../../models/venda";
import { getCombustivelDefBySigla } from "../../models/combustivel";
import { DashPanel } from "./DashPanel";
import { PanelMixCombustiveis } from "./panels/PanelMixCombustiveis";
import { PanelTicketMedioCombustiveis } from "./panels/PanelTicketMedioCombustiveis";
import { PanelCombustiveis } from "./panels/PanelCombustiveis";
import { useSpiRTCContext } from "../../components/spi-rtc";
import { MyChartDataTable } from "../../charts/MyChartDataTable";
import { PanelCombustiveisLucro } from "./panels/PanelCombustiveisLucro";
import { PanelVendaPorGrupo } from "./panels/PanelVendaPorGrupo";
import { PanelVendaPorVendedor } from "./panels/PanelVendaPorVendedor";
import { PanelAbasPorHora } from "./panels/PanelAbasPorHora";
import { PanelVendaPorEmpresa } from "./panels/PanelVendaPorEmpresa";
import { EmpresaFilter } from "./EmpresaFilter";

interface GetGraficosResponse {
  graficos: GrReport[];
  horaTotalizacao: string;
}

const extractChartData = (
  grafico: GrReport | undefined,
  comparativo: GrReport | undefined,
  detalhe: string,
  medida: string,
  name?: string,
) => {
  var compareArray: { [id: string]: number } = {};

  if (comparativo) {
    compareArray = comparativo.details[detalhe]?.series.reduce<{
      [id: string]: number;
    }>((a, b) => {
      a[b.id] = b.values[medida];
      return a;
    }, {});
  }

  const vendaCombData: MyChartData[] = grafico
    ? (grafico.details[detalhe]?.series.map((x) => {
        return {
          name: name ? x[name] : (x.sigla ?? x.name ?? x.id),
          value: x.values[medida],
          compareValue: compareArray ? compareArray[x.id] : 0,
        };
      }) ?? [])
    : [];

  return vendaCombData;
};

const extractMedidas = (
  values: { [idx: string]: number } | undefined,
): VendaMedidas => {
  return {
    quantidade: values ? values["qtd"] : 0,
    totalLiquido: values ? values["liq"] : 0,
    total: values ? values["total"] : 0,
    contagem: values ? values["count"] : 0,
    mediaLiquida: values ? values["medliq"] : 0,
    mediaQuantidade: values ? values["medqtd"] : 0,
    acrescimo: values ? values["acre"] : 0,
    desconto: values ? values["desc"] : 0,
    custo: values ? values["custo"] : 0,
    custoMedio: values ? values["customed"] : 0,
    mediaPreco: values ? values["prcmed"] : 0,
    lucroBruto: 0,
    lucroBrutoMedio: values ? values["lb"] : 0,
    margemCustoMedia: values ? (values["mgcusto"] - 1) * 100 : 0,
    margemLucroMedia: values ? values["mgven"] * 100 : 0,
    custoMedioUnitario: values ? values["customedun"] : 0,
    lucroBrutoMedioUnitario: values ? values["lbun"] : 0,
  };
};

const extractDetail = (
  grafico: GrReport | undefined,
  detalhe: string,
): Venda[] => {
  if (!grafico) return [];
  if (!grafico?.details[detalhe]) return [];

  return grafico
    ? grafico.details[detalhe]?.series.map((x) => {
        var medidas = extractMedidas(x.values);
        return {
          codigo: +x.id,
          nome: x.name,
          sigla: x.sigla,
          ...medidas,
        };
      })
    : [];
};

const useStyles = makeStyles((theme) => ({
  loadingBackdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
    backgroundColor: "rgba(0, 0, 0, 0.35)",
  },
}));

export const DashboardPage = () => {
  const classes = useStyles();
  const appContext = useAppContext();
  const [selectedEmpresas, setSelectedEmpresas] = useState<number[]>([]);

  const filtroModel = appContext.model.filtroModel;
  const dataComparativa = filtroModel?.comparableDateFilter || new Date();
  const dataComparativaEnd = filtroModel?.comparableDateFilterEnd;
  const isPeriodo = filtroModel?.dateFilterType === DateFilterType.periodo;

  const tipoPeriodoGrafico = DateFilterTypeToString(
    filtroModel?.dateFilterType ?? 1,
  );

  const dateLabel = getComparableDateStr(
    filtroModel?.dateFilter || new Date(),
    filtroModel?.dateFilterType,
  );
  const compareDateLabel =
    isPeriodo && dataComparativaEnd
      ? getComparePeriodoLabel(dataComparativa, dataComparativaEnd)
      : getComparableDateStr(dataComparativa);

  const toDateParam = (d: Date) =>
    d
      .toLocaleDateString("pt-BR")
      .split("/")
      .reverse()
      .join("-")
      .substring(0, 10);

  const empresasParam =
    selectedEmpresas.length > 0
      ? "&" + selectedEmpresas.map((id) => `empresas=${id}`).join("&")
      : "";

  const mainUrl = isPeriodo
    ? `graficos/periodo?DataInicial=${toDateParam(filtroModel!.dateFilter!)}&DataFinal=${toDateParam(filtroModel!.dateFilterEnd ?? filtroModel!.dateFilter!)}${empresasParam}`
    : `graficos/${tipoPeriodoGrafico}?dataref=${toDateParam(filtroModel?.dateFilter ?? new Date())}${empresasParam}`;

  const compareUrl = isPeriodo
    ? dataComparativaEnd
      ? `graficos/periodo?DataInicial=${toDateParam(dataComparativa)}&DataFinal=${toDateParam(dataComparativaEnd)}${empresasParam}`
      : null
    : `graficos/${tipoPeriodoGrafico}?dataref=${toDateParam(dataComparativa)}${empresasParam}`;

  const { response, mutate, isValidating } =
    useFetcher<GetGraficosResponse>(mainUrl);

  const { response: compareResp } = useFetcher<GetGraficosResponse>(
    compareUrl ?? "",
  );

  const { hub } = useSpiRTCContext();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (hub) {
      hub.on("vendaTotalizada", () => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => mutate(), 500);
      });
    }
    return () => {
      hub?.off("vendaTotalizada");
      clearTimeout(debounceRef.current);
    };
  }, [hub, mutate]);

  const graficoVendas = response?.data.graficos[0];
  const graficoAbastecimentos = response?.data.graficos[2];

  var totais: { [idx: string]: number } = {};
  if (graficoVendas)
    Object.keys(graficoVendas!.total).forEach((x) => {
      totais[x] = graficoVendas!.total[x]?.v || 0;
    });

  const totaisVenda = extractMedidas(totais);
  const totaisCombustiveis = extractMedidas(
    graficoVendas?.details["por_combustivel"]?.total,
  );
  const totaisProdutos = extractMedidas(
    graficoVendas?.details["por_grupo_produto"]?.total,
  );
  const vendaPorCombustivel = extractDetail(graficoVendas, "por_combustivel");
  const vendaPorEmpresa = extractDetail(graficoVendas, "por_empresa");
  //const abastecPorCombustivel = extractDetail(graficoAbastecimentos, 'por_combustivel');
  const vendaPorGrupoProduto = extractDetail(
    graficoVendas,
    "por_grupo_produto",
  );
  const vendaTop10Produto = extractDetail(graficoVendas, "top_produtos_liq");
  const vendaPorVendedor = extractDetail(graficoVendas, "por_vendedor");

  const graficoVendasComparativo = compareResp?.data.graficos[0];
  const graficoAbastecimentosComparativo = compareResp?.data.graficos[2];

  var totaisComparativo: { [idx: string]: number } = {};
  if (graficoVendasComparativo)
    Object.keys(graficoVendasComparativo!.total).forEach((x) => {
      totaisComparativo[x] = graficoVendasComparativo!.total[x]?.v || 0;
    });

  const totaisVendaComparativo = extractMedidas(totaisComparativo);
  const totaisCombustiveisComparativo = extractMedidas(
    graficoVendasComparativo?.details["por_combustivel"]?.total,
  );
  const totaisProdutosComparativo = extractMedidas(
    graficoVendasComparativo?.details["por_grupo_produto"]?.total,
  );
  const vendaPorCombustivelComparativo = extractDetail(
    graficoVendasComparativo,
    "por_combustivel",
  );
  const vendaPorVendedorComparativo = extractDetail(
    graficoVendasComparativo,
    "por_vendedor",
  );

  const graficoCombustiveisColorArray = vendaPorCombustivel.map(
    (x) => getCombustivelDefBySigla(x.sigla!)?.cor ?? "",
  );

  var abasPorHora: Venda[] | undefined = undefined;
  var abasPorHoraComparativo: Venda[] | undefined = undefined;
  var vendaPorDia: MyChartData[] | undefined = undefined;

  if (appContext.model.filtroModel?.dateFilterType === DateFilterType.diario) {
    abasPorHora = extractDetail(graficoAbastecimentos, "por_hora");
    abasPorHoraComparativo = extractDetail(
      graficoAbastecimentosComparativo,
      "por_hora",
    );
  } else {
    vendaPorDia = extractChartData(
      graficoVendas,
      graficoVendasComparativo,
      "por_dia",
      "qtd",
      "id",
    );
  }

  const vendaPorForma: MyChartData[] = extractChartData(
    graficoVendas,
    graficoVendasComparativo,
    "por_forma",
    "liq",
  );

  return (
    <div>
      <Backdrop className={classes.loadingBackdrop} open={isValidating}>
        <CircularProgress color="inherit" size={60} />
      </Backdrop>
      <DateFilter
        dataAtualizacao={
          response?.data?.horaTotalizacao
            ? new Date(response.data.horaTotalizacao)
            : undefined
        }
        extraFilter={
          <EmpresaFilter
            selected={selectedEmpresas}
            onChange={setSelectedEmpresas}
          />
        }
      />
      <Divider style={{ margin: "4px -8px" }} />
      <Container
        maxWidth={false}
        style={{ paddingTop: "12px", paddingBottom: "20px" }}
      >
        <Grid container spacing={3}>
          <Grid item lg={3} sm={6} xl={3} xs={6}>
            <CardKPI
              nome="Faturamento Total"
              indicador={{
                valor: totaisVenda.totalLiquido,
                formatacao: ",0",
                prefixo: "R$",
              }}
              comparativo={{
                valorBase: totaisVenda.totalLiquido,
                valor: totaisVendaComparativo.totalLiquido,
                data: dataComparativa!,
              }}
            />
          </Grid>
          <Grid item lg={3} sm={6} xl={3} xs={6}>
            <CardKPI
              nome="Custo Médio"
              indicador={{
                valor: totaisVenda.custoMedio,
                formatacao: ",0",
                prefixo: "R$",
              }}
              comparativo={{
                valorBase: totaisVenda.custoMedio,
                valor: totaisVendaComparativo.custoMedio,
                data: dataComparativa!,
              }}
            />
          </Grid>
          <Grid item lg={3} sm={6} xl={3} xs={6}>
            <CardKPI
              nome="Lucro Bruto"
              indicador={{
                valor: totaisVenda.lucroBrutoMedio,
                formatacao: ",0",
                prefixo: "R$",
              }}
              comparativo={{
                valorBase: totaisVenda.lucroBrutoMedio,
                valor: totaisVendaComparativo.lucroBrutoMedio,
                data: dataComparativa!,
              }}
            />
          </Grid>
          <Grid item lg={3} sm={6} xl={3} xs={6}>
            <CardKPI
              nome="Contagem de vendas"
              indicador={{ valor: totaisVenda.contagem, formatacao: ",0" }}
              comparativo={{
                valorBase: totaisVenda.contagem,
                valor: totaisVendaComparativo.contagem,
                data: dataComparativa!,
              }}
            />
          </Grid>
          <Grid item lg={4} md={6} xl={4} xs={12}>
            <PanelCombustiveis
              vendaCombustiveis={vendaPorCombustivel}
              vendaComparativo={vendaPorCombustivelComparativo}
              totaisCombustiveis={totaisCombustiveis}
              totaisComparativo={totaisCombustiveisComparativo}
            />
          </Grid>
          <Grid item lg={4} md={6} xl={4} xs={12}>
            <PanelMixCombustiveis
              vendaCombustiveis={vendaPorCombustivel}
              totalLiquido={totaisCombustiveis.totalLiquido}
              totalLitragem={totaisCombustiveis.quantidade}
              colorArray={graficoCombustiveisColorArray}
            />
          </Grid>
          <Grid item lg={4} md={6} xl={4} xs={12}>
            <PanelTicketMedioCombustiveis
              vendaCombustiveis={vendaPorCombustivel}
              comparativo={vendaPorCombustivelComparativo}
              totaisCombustiveis={totaisCombustiveis}
              totaisComparativo={totaisCombustiveisComparativo}
              colorArray={graficoCombustiveisColorArray}
            />
          </Grid>
          <Grid item lg={4} md={6} xl={4} xs={12}>
            <PanelCombustiveisLucro
              vendaCombustiveis={vendaPorCombustivel}
              vendaComparativo={vendaPorCombustivelComparativo}
              totaisCombustiveis={totaisCombustiveis}
              totaisComparativo={totaisCombustiveisComparativo}
            />
          </Grid>
          <Grid item lg={8} md={12} xl={8} xs={12}>
            {abasPorHora ? (
              <PanelAbasPorHora
                abasPorHora={abasPorHora!}
                comparativo={abasPorHoraComparativo!}
                dateLabel={dateLabel}
                compareDateLabel={compareDateLabel}
              />
            ) : vendaPorDia ? (
              <DashPanel title="Vendas por Dia">
                <LineChart
                  label={dateLabel}
                  compareLabel={compareDateLabel}
                  dados={vendaPorDia}
                />
              </DashPanel>
            ) : (
              <></>
            )}
          </Grid>
          {vendaPorEmpresa.length > 1 && (
            <Grid item lg={12} md={12} xl={12} xs={12}>
              <PanelVendaPorEmpresa vendaPorEmpresa={vendaPorEmpresa} />
            </Grid>
          )}
          <Grid item lg={4} md={6} xl={4} xs={12}>
            <PanelVendaPorGrupo
              vendaPorGrupoProduto={vendaPorGrupoProduto}
              totaisProdutos={totaisProdutos}
              totaisComparativo={totaisProdutosComparativo}
            />
          </Grid>
          <Grid item lg={4} md={6} xl={4} xs={12}>
            <DashPanel title="Top 10 produtos">
              <MyChartDataTable
                rows={vendaTop10Produto.map((x) => ({
                  name: x.nome,
                  value: x.totalLiquido,
                }))}
                format=",0.00"
                prefix="R$"
              />
            </DashPanel>
          </Grid>
          <Grid item lg={4} md={6} xl={4} xs={12}>
            <PanelVendaPorVendedor
              vendaPorVendedor={vendaPorVendedor}
              vendaComparativo={vendaPorVendedorComparativo}
            />
          </Grid>
          <Grid item lg={4} md={12} xl={4} xs={12}>
            <DashPanel title="Formas de Pagamentos">
              <BarChart
                label="forma"
                dados={vendaPorForma}
                horizontal={true}
                colors={["#3487A8", "#4CE0B4", "#3CC7C7", "#39C471", "#3ED64D"]}
              />
            </DashPanel>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};
